<template>
  <v-navigation-drawer
    v-model="isOpen"
    location="right"
    temporary
    width="400"
    class="activity-feed-panel"
  >
    <!-- Header -->
    <v-toolbar
      color="primary"
      dark
    >
      <v-toolbar-title>Лента событий</v-toolbar-title>
      <v-spacer />
      <v-btn
        v-if="unreadCount > 0"
        variant="text"
        size="small"
        @click="handleMarkAllAsRead"
      >
        Прочитать все
      </v-btn>
      <v-btn
        icon
        @click="closePanel"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Content -->
    <v-list
      v-if="items.length > 0"
      class="pa-0"
    >
      <template
        v-for="item in items"
        :key="item.id"
      >
        <v-list-item
          :class="{ 'unread': !item.isRead }"
          class="activity-item"
          @click="handleItemClick(item)"
        >
          <template #prepend>
            <v-avatar
              :color="getTypeColor(item.type)"
              size="40"
              class="mr-3"
            >
              <v-icon color="white">
                {{ getTypeIcon(item.type) }}
              </v-icon>
            </v-avatar>
          </template>

          <v-list-item-title class="text-subtitle-2 font-weight-medium d-flex align-center flex-wrap">
            <span>{{ item.title }}</span>
            <v-chip
              v-if="item.data?.taskHumanId"
              size="small"
              color="primary"
              variant="tonal"
              class="ml-2 task-link-chip"
              @click.stop="navigateToTask(item)"
            >
              <v-icon size="x-small" class="mr-1">mdi-open-in-new</v-icon>
              {{ item.data.taskHumanId }}
            </v-chip>
          </v-list-item-title>

          <!-- Task title (body) - full display -->
          <v-list-item-subtitle
            v-if="item.body"
            class="text-body-2 mt-1 task-title-line"
          >
            <v-icon size="x-small" class="mr-1">mdi-checkbox-marked-outline</v-icon>
            {{ item.body }}
          </v-list-item-subtitle>

          <!-- Status change details -->
          <v-list-item-subtitle
            v-if="item.data?.oldStatus && item.data?.newStatus"
            class="text-caption mt-1 status-change-line"
          >
            <v-chip size="x-small" :color="getStatusColor(item.data.oldStatus as string)" variant="flat" class="mr-1">
              {{ getStatusLabel(item.data.oldStatus as string) }}
            </v-chip>
            <v-icon size="x-small">mdi-arrow-right</v-icon>
            <v-chip size="x-small" :color="getStatusColor(item.data.newStatus as string)" variant="flat" class="ml-1">
              {{ getStatusLabel(item.data.newStatus as string) }}
            </v-chip>
          </v-list-item-subtitle>

          <v-list-item-subtitle class="text-caption mt-1">
            <span v-if="item.actor">{{ item.actor.displayName }} · </span>
            {{ formatTime(item.lastEventAt) }}
          </v-list-item-subtitle>

          <template #append>
            <div class="d-flex flex-column align-end">
              <v-chip
                v-if="item.groupCount > 1"
                size="x-small"
                color="primary"
                variant="tonal"
                class="mb-1"
                @click.stop="handleExpandGroup(item.id)"
              >
                {{ item.groupCount }}
                <v-icon
                  size="x-small"
                  class="ml-1"
                >
                  {{ expandedGroups.has(item.id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-chip>
              <v-icon
                v-if="!item.isRead"
                color="primary"
                size="small"
              >
                mdi-circle
              </v-icon>
            </div>
          </template>
        </v-list-item>

        <!-- Expanded group items -->
        <template v-if="expandedGroups.has(item.id)">
          <v-list-item
            v-for="child in groupedItems.get(item.id) || []"
            :key="child.id"
            class="pl-16 activity-item-child"
            density="compact"
          >
            <v-list-item-subtitle class="text-caption">
              {{ child.title }}
              <span v-if="child.actor"> · {{ child.actor.displayName }}</span>
              <span class="text-disabled"> · {{ formatTime(child.createdAt) }}</span>
            </v-list-item-subtitle>
          </v-list-item>
        </template>

        <v-divider v-if="items.indexOf(item) < items.length - 1" />
      </template>
    </v-list>

    <!-- Empty state -->
    <v-container
      v-else-if="!loading"
      class="text-center py-12"
    >
      <v-icon
        size="64"
        color="grey-lighten-1"
      >
        mdi-bell-off-outline
      </v-icon>
      <p class="text-body-2 text-grey mt-4">
        Нет новых событий
      </p>
    </v-container>

    <!-- Loading -->
    <v-container
      v-if="loading"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-container>

    <!-- Load more -->
    <v-container
      v-if="hasMore && !loading"
      class="text-center pb-4"
    >
      <v-btn
        variant="text"
        color="primary"
        size="small"
        @click="loadMore"
      >
        Загрузить ещё
      </v-btn>
    </v-container>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useActivityFeedStore } from '@/stores/activityFeed'
import { useRouter } from 'vue-router'
import type { NotificationType, ActivityFeedItem } from '@/plugins/api'

const router = useRouter()
const activityFeedStore = useActivityFeedStore()

const {
  items,
  unreadCount,
  loading,
  hasMore,
  isOpen,
  expandedGroups,
  groupedItems,
} = storeToRefs(activityFeedStore)

const {
  closePanel,
  loadMore,
  markAsRead,
  markAllAsRead,
  expandGroup,
} = activityFeedStore

// Type icons
const typeIcons: Record<NotificationType, string> = {
  task_assigned: 'mdi-account-arrow-right',
  task_status_changed: 'mdi-swap-horizontal',
  task_deadline_approaching: 'mdi-clock-alert',
  task_overdue: 'mdi-clock-alert-outline',
  task_comment_added: 'mdi-comment-text',
  gitlab_commit_linked: 'mdi-source-commit',
  ai_analysis_ready: 'mdi-robot',
  timer_stopped: 'mdi-timer-stop',
  commit_no_time_tracking: 'mdi-source-commit-local',
}

// Type colors
const typeColors: Record<NotificationType, string> = {
  task_assigned: 'blue',
  task_status_changed: 'purple',
  task_deadline_approaching: 'orange',
  task_overdue: 'red',
  task_comment_added: 'green',
  gitlab_commit_linked: 'orange-darken-2',
  ai_analysis_ready: 'teal',
  timer_stopped: 'grey',
  commit_no_time_tracking: 'amber',
}

function getTypeIcon(type: NotificationType): string {
  return typeIcons[type] || 'mdi-bell'
}

function getTypeColor(type: NotificationType): string {
  return typeColors[type] || 'grey'
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} мин. назад`
  if (hours < 24) return `${hours} ч. назад`
  if (days < 7) return `${days} д. назад`

  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  })
}

// Status colors and labels
const statusColors: Record<string, string> = {
  draft: 'grey',
  pending: 'blue-grey',
  in_progress: 'blue',
  testing: 'purple',
  completed: 'green',
  on_hold: 'orange',
  blocked: 'red',
  cancelled: 'grey-darken-1',
  deleted: 'grey-darken-2',
  merged: 'teal',
}

const statusLabels: Record<string, string> = {
  draft: 'Черновик',
  pending: 'Ожидает',
  in_progress: 'В работе',
  testing: 'Тестирование',
  completed: 'Завершена',
  on_hold: 'Приостановлена',
  blocked: 'Заблокирована',
  cancelled: 'Отменена',
  deleted: 'Удалена',
  merged: 'Объединена',
}

function getStatusColor(status: string): string {
  return statusColors[status] || 'grey'
}

function getStatusLabel(status: string): string {
  return statusLabels[status] || status
}

function navigateToTask(item: ActivityFeedItem) {
  if (item.entityType === 'task' && item.entityId) {
    if (!item.isRead) {
      markAsRead([item.id])
    }
    router.push(`/tasks/${item.entityId}`)
    closePanel()
  }
}

function handleItemClick(item: ActivityFeedItem) {
  // Mark as read
  if (!item.isRead) {
    markAsRead([item.id])
  }

  // Navigate to entity based on type
  if (item.type === 'commit_no_time_tracking' && item.data?.actionUrl) {
    // Navigate to time tracking with pre-filled commit SHA
    router.push(item.data.actionUrl as string)
    closePanel()
  } else if (item.type === 'commit_no_time_tracking' && item.data?.commitSha) {
    // Fallback: use commitSha directly
    router.push(`/time-tracking?commitSha=${item.data.commitSha}`)
    closePanel()
  } else if (item.entityType === 'task' && item.entityId) {
    router.push(`/tasks/${item.entityId}`)
    closePanel()
  } else if (item.entityType === 'article' && item.entityId) {
    router.push(`/knowledge/${item.entityId}`)
    closePanel()
  } else if (item.entityType === 'project' && item.entityId) {
    router.push(`/projects/${item.entityId}`)
    closePanel()
  }
}

function handleExpandGroup(parentId: string) {
  expandGroup(parentId)
}

function handleMarkAllAsRead() {
  markAllAsRead()
}
</script>

<style scoped>
.activity-feed-panel {
  z-index: 2000;
}

.activity-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.activity-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.04);
}

.activity-item.unread {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.activity-item-child {
  background-color: rgba(0, 0, 0, 0.02);
}

.task-link-chip {
  cursor: pointer;
  font-weight: 500;
}

.task-link-chip:hover {
  opacity: 0.85;
}

.task-title-line {
  white-space: normal !important;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.8);
}

.status-change-line {
  display: flex;
  align-items: center;
  gap: 2px;
}
</style>
